/** The authenticated app chrome: header (nav toggle + brand + user slot) and nav + content. */

import { useCallback, useEffect, useState } from "react";
import type { ReactNode } from "react";

export interface AppShellProps {
  productName: string;
  /** Absolute URL/path to the instance logo, or null. */
  logoSrc?: string | null;
  /** Navigation slot (the app supplies its router links). */
  nav: ReactNode;
  /** User/account slot on the right of the header (name, logout). */
  userSlot: ReactNode;
  children: ReactNode;
}

/** Below this width the navigation stacks above the content and starts closed. */
export const NAV_NARROW_PX = 900;
const NAV_STORAGE_KEY = "pbxmgr.nav.open";

function isNarrow(): boolean {
  return typeof window !== "undefined" && window.innerWidth < NAV_NARROW_PX;
}

/** The remembered choice, else open on a wide window and closed on a narrow one. */
function initialNavOpen(): boolean {
  try {
    const stored = window.localStorage.getItem(NAV_STORAGE_KEY);
    if (stored === "1") return true;
    if (stored === "0") return false;
  } catch {
    // storage blocked (private window): fall through to the width default
  }
  return !isNarrow();
}

/**
 * Router-agnostic layout: the app passes nav links and the user menu as slots.
 *
 * The navigation is hideable (the ☰ button; the choice is remembered per
 * browser). On a narrow window it stacks above the content and closes after
 * a link is followed, so the page is never wider than the window.
 */
export function AppShell({ productName, logoSrc, nav, userSlot, children }: AppShellProps): ReactNode {
  const [navOpen, setNavOpen] = useState<boolean>(initialNavOpen);

  const toggle = useCallback((): void => {
    setNavOpen((open) => {
      const next = !open;
      try {
        window.localStorage.setItem(NAV_STORAGE_KEY, next ? "1" : "0");
      } catch {
        // not remembered; the toggle still works for this page view
      }
      return next;
    });
  }, []);

  // Escape closes the stacked (narrow) navigation, like any disclosure.
  useEffect(() => {
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape" && isNarrow()) setNavOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const onNavClick = (event: React.MouseEvent<HTMLElement>): void => {
    // Following a link on a narrow window closes the stacked nav (not remembered).
    if (isNarrow() && (event.target as HTMLElement).closest("a")) setNavOpen(false);
  };

  return (
    <div className={`ui-shell ${navOpen ? "ui-shell--nav-open" : "ui-shell--nav-closed"}`}>
      <header className="ui-shell__header">
        <button
          type="button"
          className="ui-shell__nav-toggle"
          aria-label={navOpen ? "Hide navigation" : "Show navigation"}
          aria-expanded={navOpen}
          aria-controls="ui-shell-nav"
          onClick={toggle}
        >
          <span aria-hidden="true">☰</span>
        </button>
        <div className="ui-shell__brand">
          {logoSrc ? <img src={logoSrc} alt={productName} /> : null}
          <span>{productName}</span>
        </div>
        <div className="ui-shell__spacer" />
        <div className="ui-shell__user">{userSlot}</div>
      </header>
      <div className="ui-shell__body">
        <nav id="ui-shell-nav" className="ui-shell__nav" hidden={!navOpen} onClick={onNavClick}>
          {nav}
        </nav>
        <main className="ui-shell__content">{children}</main>
      </div>
    </div>
  );
}
